import React from 'react';

/**
 * Catches render errors so Executive never white-screens.
 * Shows message + stack snippet in a red card.
 */
export default class ExecutiveErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Executive dashboard render error', error, info);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const stack = (info?.componentStack || error?.stack || '').toString().slice(0, 800);
    const title = this.props.title || 'Workbench Executive';

    return (
      <div className="exec-dash p-3 md:p-4" style={{ minHeight: '60vh' }}>
        <header className="exec-header">
          <div className="exec-header-top">
            <h1>{title}</h1>
            <div className="exec-meta">
              <button
                type="button"
                className="btn-refresh"
                onClick={() => {
                  this.setState({ error: null, info: null });
                  if (typeof this.props.onRetry === 'function') this.props.onRetry();
                  else if (typeof window !== 'undefined') window.location.reload();
                }}
              >
                Refresh now
              </button>
            </div>
          </div>
        </header>
        <div
          className="exec-crash-card"
          role="alert"
          style={{
            background: '#fef2f2',
            border: '2px solid #dc2626',
            borderRadius: 10,
            padding: '1rem 1.25rem',
            color: '#991b1b',
            marginBottom: '1rem',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8 }}>
            Executive page crashed while rendering
          </div>
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, marginBottom: 8 }}>
            {error?.message || String(error)}
          </div>
          {stack ? (
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 11,
                background: '#fff',
                border: '1px solid #fecaca',
                borderRadius: 6,
                padding: 8,
                maxHeight: 220,
                overflow: 'auto',
                margin: 0,
              }}
            >
              {stack}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}

/** Narrow boundary around a single chart — falls back to empty-state, keeps KPIs visible. */
export class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.warn('Executive chart failed', error);
  }

  render() {
    if (this.state.error) {
      return (
        this.props.fallback || (
          <div className="empty-state">Chart unavailable — {this.state.error.message || 'render error'}</div>
        )
      );
    }
    return this.props.children;
  }
}
